/* eslint-disable no-lonely-if */
import React, { useCallback, useMemo, useState } from 'react';
import { Text, Flex, Label, Content, Button, TextArea } from '@leverege/ui-elements';
import { TitleBar } from '@leverege/ui-plugin';
import { Attributes } from '@leverege/ui-attributes';
import { Config } from '@leverege/plugin';
import { GlobalState } from '@leverege/ui-redux';
import DeviceInput from './DeviceInput';
import Scan from './Scan';
import DeviceSearch from './DeviceSearch';
import SalesFlowConstants from '../salesFlow/Constants';
import S from './PairingFlow.css';
import IntermediateScreen from './intermediate/IntermediateScreen';
import { getLocatorLocationAndCompany, getVehicleLocationAndCompany } from '../../util/Interface';
import Util from '../salesFlow/Util';
import checkForLostRecords from '../../util/Lost';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function PairForm(props) {
  const {
    assetId,
    trackerId,
    loading,
    trackerInstallLocation,
    mobile,
    onChange: pOnChange,
    onPair,
    headerClassName,
    areaClassName,
    footerClassName,
    AddDeviceForm,
    actions,
    onClose,
    parentNetworkId,
    titleVariant,
    titleIcon,
    location
  } = props;
  const [activeScreen, setActiveScreen] = useState('main');
  const [activeField, setActiveField] = useState();
  const [intermediateType, setIntermediateType] = useState();
  const [selectedObject, setSelectedObject] = useState();
  const locatorOfflineWindow = Config.get('Pairing', 'locatorOfflineWindow', 1000 * 60 * 60 * 3);
  const DISABLED_CONDITIONS = useMemo(() => {
    return ['or', [{
      attribute: ['locator.batteryLevel', 'locator'],
      condition: '<',
      value: 70,
      message: 'Locator battery below 70%',
      disable: false
    }, {
      attribute: ['locator.keystreamDeviceState', 'locator'],
      condition: '!=',
      value: SalesFlowConstants.KEYSTREAM_ACTIVATED,
      message: 'Locator unactivated',
      checkNull: true,
      disable: false
    }, {
      attribute: ['locator.lastHeartbeat', 'locator'],
      condition: '<',
      value: Date.now() - locatorOfflineWindow,
      message: 'Locator Offline',
      disable: false
    }]];
  }, [locatorOfflineWindow]);
  const onClick = useCallback(({
    data
  }) => {
    setActiveScreen(data.screen);
    setActiveField(data.field);
  });
  const onFocusAsset = useCallback(({
    data
  }) => {
    setActiveScreen(data.screen);
    setActiveField(data.field);
  });
  const onFocusTracker = useCallback(({
    data
  }) => {
    setActiveScreen(data.screen);
    setActiveField(data.field);
  });
  const onChange = async evt => {
    const {
      data,
      object
    } = evt;
    const cmp = Attributes.get('location.company', 'location', location.data);
    if (!data) {
      setActiveScreen('main');
      setActiveField(null);
      pOnChange(evt);
      return;
    }
    if (data === 'trackerInstallLocation') {
      pOnChange(evt);
      return;
    }
    if (data === 'locatorFound') {
      setActiveScreen('main');
      setActiveField(null);
      pOnChange(evt);
      return;
    }
    if (cmp) {
      if (data === 'assetId') {
        if (object && object?.data?.location?.id !== location.id) {
          const res = await getVehicleLocationAndCompany(object.id, {
            actions,
            location
          });
          setSelectedObject(res);
          setActiveScreen('intermediate');
          setActiveField(null);
          setIntermediateType('vehicleElsewhere');
          pOnChange(evt);
          return;
        }
        if (object?.data?.locator?.id) {
          setSelectedObject(object);
          setActiveScreen('intermediate');
          setActiveField(null);
          setIntermediateType('vehiclePaired');
          pOnChange(evt);
          return;
        }
      } else {
        const res = await getLocatorLocationAndCompany(object.id, {
          actions,
          location
        });
        const {
          location: loc,
          locator
        } = res;
        const isLocatorElsewhere = location.id !== loc?.id;
        const isLocatorPaired = !!locator?.data?.vehicle?.id;
        if (isLocatorElsewhere && isLocatorPaired) {
          setSelectedObject({
            ...res,
            assetId
          });
          setActiveScreen('intermediate');
          setActiveField(null);
          setIntermediateType('locatorPairedElsewhere');
          pOnChange(evt);
          return;
        }
        if (isLocatorPaired) {
          const existingVehicle = GlobalState.get()?.models?.lookup?.[locator.data.vehicle.id];
          // Fetch vehicle that the locator is paired to
          if (!existingVehicle) {
            // Try to fetch the vehicle, but keep in mind that the vehicle may not be in the current location and we might not have access to it.
            // (needed to display its VIN in the intermediate screen)
            GlobalState.dispatch(actions.parent().child('vehicles').item(locator.data.vehicle.id).get());
          }
          setSelectedObject({
            ...res,
            assetId
          });
          setActiveScreen('intermediate');
          setActiveField(null);
          setIntermediateType('locatorPaired');
          pOnChange(evt);
          return;
        }
        if (isLocatorElsewhere) {
          setSelectedObject(res);
          setActiveScreen('intermediate');
          setActiveField(null);
          setIntermediateType('locatorElsewhere');
          pOnChange(evt);
          return;
        }
        if (!Util.isLocatorActivated(locator)) {
          setSelectedObject(res);
          setActiveScreen('intermediate');
          setActiveField(null);
          setIntermediateType('locatorUnactivated');
          pOnChange(evt);
          return;
        }
        if (Util.isLocatorOffline(locator)) {
          setSelectedObject(res);
          setActiveScreen('intermediate');
          setActiveField(null);
          setIntermediateType('locatorOffline');
          pOnChange(evt);
          return;
        }

        // check if the locator is currently lost and has a lost record
        const {
          isAllLost
        } = checkForLostRecords(locator);
        if (isAllLost) {
          setSelectedObject(res);
          setActiveScreen('intermediate');
          setActiveField(null);
          setIntermediateType('locatorLost');
          pOnChange(evt);
          return;
        }
      }
    } else {
      if (data === 'assetId') {
        if (object?.data?.locator?.id) {
          setSelectedObject(object);
          setActiveScreen('intermediate');
          setActiveField(null);
          setIntermediateType('vehiclePaired');
          pOnChange(evt);
          return;
        }
      }
    }
    setActiveScreen('main');
    setActiveField(null);
    pOnChange(evt);
  };
  const trackerInstallLocationRequired = Config.get('PairForm', 'trackerInstallLocationRequired', true);
  let header;
  let content;
  let footer;
  let body = null;
  switch (activeScreen) {
    case 'intermediate':
      body = /*#__PURE__*/_jsx(IntermediateScreen, {
        ...props,
        type: intermediateType,
        onClose: onClose,
        onOkay: onChange,
        onSetField: pOnChange,
        selectedObject: selectedObject,
        areaClassName: areaClassName,
        headerClassName: headerClassName,
        footerClassName: footerClassName
      });
      break;
    case 'add':
      body = /*#__PURE__*/_jsx(AddDeviceForm, {
        mobile: mobile,
        headerClassName: headerClassName,
        areaClassName: areaClassName,
        footerClassName: footerClassName,
        actions: actions,
        target: {
          type: 'vehicle',
          data: {
            networkAliases: {
              [parentNetworkId]: {
                deviceId: assetId
              }
            },
            data: {
              vin: assetId
            }
          }
        },
        onSuccess: res => {
          onChange({
            data: activeField,
            value: res[0]?.data?.vin
          });
        },
        onCancel: () => {
          onChange({
            data: activeField,
            value: null
          });
        }
      });
      break;
    case 'scan':
      content = /*#__PURE__*/_jsx(Scan, {
        ...props,
        eventData: activeField,
        onClick: onClick,
        onChange: onChange
      });
      break;
    case 'search':
      {
        const {
          [activeField]: val
        } = props;
        body = /*#__PURE__*/_jsx(DeviceSearch, {
          ...props,
          eventData: activeField,
          value: val,
          onChange: onChange,
          onClick: onClick,
          disabledConditions: activeField === 'trackerId' ? DISABLED_CONDITIONS : undefined
        });
        break;
      }
    default:
      header = /*#__PURE__*/_jsx(TitleBar, {
        variant: titleVariant,
        title: "Pair Locator to Vehicle",
        icon: titleIcon
      });
      content = /*#__PURE__*/_jsxs(Flex, {
        variant: "colL",
        children: [/*#__PURE__*/_jsx(Text, {
          className: S.body,
          children: "Choose which locator and vehicle you'd like to pair."
        }), /*#__PURE__*/_jsx(Text, {
          variant: "tooltip",
          children: "The VIN barcode can be found in the driver-side door for most modern models."
        }), /*#__PURE__*/_jsxs(Flex, {
          variant: "colS",
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "inputTitle",
            children: "Locator ID"
          }), /*#__PURE__*/_jsx(DeviceInput, {
            "data-test-postfix": "Locator",
            hint: "Search Locator",
            eventData: "trackerId",
            value: trackerId,
            onChange: onChange,
            onFocus: onFocusTracker,
            onClick: onClick
          })]
        }), /*#__PURE__*/_jsxs(Flex, {
          variant: "colS",
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "inputTitle",
            children: "Vehicle"
          }), /*#__PURE__*/_jsx(DeviceInput, {
            "data-test-postfix": "VIN",
            hint: "Search VIN",
            eventData: "assetId",
            value: assetId,
            onChange: onChange,
            onFocus: onFocusAsset,
            onClick: onClick
          })]
        }), /*#__PURE__*/_jsxs(Flex, {
          variant: "colS",
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "inputTitle",
            children: "Locator Placement Notes"
          }), /*#__PURE__*/_jsx(TextArea, {
            "data-test-id": "input.pairing.trackerInstallLocation",
            eventData: "trackerInstallLocation",
            onChange: onChange,
            value: trackerInstallLocation
          })]
        })]
      });
      footer = /*#__PURE__*/_jsxs(React.Fragment, {
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: onClose,
          variant: "secondary",
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          "data-test-id": "button.pairing.pair",
          variant: "primary",
          disabled: !assetId || !trackerId || trackerInstallLocationRequired && !trackerInstallLocation || loading,
          onClick: onPair,
          children: "Pair"
        })]
      });
  }
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [header && /*#__PURE__*/_jsxs(Content.Header, {
      children: [header, /*#__PURE__*/_jsx(Button, {
        variant: "closeDialog",
        className: S.close,
        onClick: onClose
      })]
    }), content && /*#__PURE__*/_jsx(Content.Area, {
      className: areaClassName,
      children: content
    }), footer && /*#__PURE__*/_jsx(Content.Footer, {
      className: footerClassName,
      layout: "flex:rowMEnd",
      children: footer
    }), body]
  });
}